/**
 * Table search: client-side row filters, the per-column header search popover,
 * and the global quick-filter toolbar.
 */
import { useState, type MouseEvent, type ReactNode, type Ref, type SyntheticEvent } from 'react';
import { Box, IconButton, InputAdornment, Popover, TextField } from '@mui/material';
import { QuickFilter, QuickFilterClear, QuickFilterControl, Toolbar, type GridColumnHeaderParams, type GridValidRowModel } from '@mui/x-data-grid';
import { CancelIcon, SearchIcon } from '../../icons';
import { toCellString } from '../../utils/format';
import { HFButton } from '../../atoms/HFButton';
import { HFChip } from '../../atoms/HFChip';
import { HFTextField } from '../../atoms/HFTextField';
import { hfAlpha } from '../../theme';
import { HF_HEADER_TITLE_CLASS } from './columnSizing';

/**
 * Client-side AND filter across fields (case-insensitive substring).
 * Empty query values are ignored. Use when simultaneous per-column filters
 * are needed — Community DataGrid only allows one filter-model item.
 */
export function filterRowsByFields<R extends GridValidRowModel>(rows: R[], queries: Partial<Record<string, string>>): R[] {
  const entries = Object.entries(queries).filter(([, q]) => q != null && String(q).trim() !== '');
  if (entries.length === 0) return rows;

  return rows.filter((row) =>
    entries.every(([field, query]) => {
      const hay = toCellString((row as Record<string, unknown>)[field]).toLowerCase();
      return hay.includes(String(query).toLowerCase());
    }),
  );
}

/** Client-side global quick filter: case-insensitive substring across every cell. */
export function filterRowsByQuickQuery<R extends GridValidRowModel>(rows: R[], query: string): R[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) => Object.values(row as Record<string, unknown>).some((value) => toCellString(value).toLowerCase().includes(needle)));
}

function toTime(value: unknown): number | null {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  const t = date.getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Inclusive date-range filter for a single field.
 * Prefer this over the grid filter model when you need both `from` and `to`
 * (Community only allows one filter item at a time).
 */
export function filterRowsByDateRange<R extends GridValidRowModel>(rows: R[], field: string, from?: Date | null, to?: Date | null): R[] {
  const fromT = from ? from.getTime() : null;
  const toT = to ? to.getTime() : null;
  if (fromT == null && toT == null) return rows;

  return rows.filter((row) => {
    const t = toTime((row as Record<string, unknown>)[field]);
    if (t == null) return false;
    if (fromT != null && t < fromT) return false;
    if (toT != null && t > toT) return false;
    return true;
  });
}

export type ColumnSearchHeaderProps<R extends GridValidRowModel> = {
  params: GridColumnHeaderParams<R>;
  value: string;
  onChange: (field: string, query: string) => void;
  /** Extra header content (e.g. drag handle). */
  startAdornment?: ReactNode;
};

/** Column title + magnifier that opens a small search popover anchored to it. */
export function ColumnSearchHeader<R extends GridValidRowModel>({ params, value, onChange, startAdornment }: ColumnSearchHeaderProps<R>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState(value);
  const field = params.field;
  const title = params.colDef.headerName ?? field;
  const active = Boolean(value);
  const open = Boolean(anchorEl);

  const openPopover = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setDraft(value);
    setAnchorEl(event.currentTarget);
  };

  const close = () => setAnchorEl(null);

  const apply = (query: string) => {
    onChange(field, query);
    close();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        width: '100%',
      }}
    >
      {startAdornment}
      <Box
        component="span"
        className={HF_HEADER_TITLE_CLASS}
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {title}
      </Box>
      {active ? (
        <HFChip
          size="small"
          variant="outlined"
          color="primary"
          icon={<SearchIcon />}
          label={value}
          title={`${title}: ${value}`}
          aria-label={`Edit ${title} filter: ${value}`}
          onClick={(event) => {
            event.stopPropagation();
            openPopover(event);
          }}
          onDelete={(event: SyntheticEvent) => {
            event.stopPropagation();
            onChange(field, '');
          }}
          onKeyDown={(event) => event.stopPropagation()}
          deleteIcon={<CancelIcon aria-label={`Clear ${title} filter`} />}
          sx={{
            // top spacing exception: chip must fit the 40px column header
            flex: '0 1 auto',
            minWidth: 0,
            maxWidth: '55%',
            height: 20,
            borderRadius: 1,
            borderWidth: 1.5,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0,
            textTransform: 'none',
            '& .MuiChip-label': { px: 0.75, py: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            '& .MuiChip-icon': { fontSize: 12, ml: 0.5, mr: -0.25, color: 'inherit' },
            '& .MuiChip-deleteIcon': {
              fontSize: 14,
              mr: 0.25,
              color: 'inherit',
              opacity: 0.8,
              '&:hover': { opacity: 1, color: 'inherit' },
            },
            '&:hover': {
              bgcolor: (theme) => hfAlpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.32 : 0.16),
            },
          }}
        />
      ) : (
        <IconButton
          size="small"
          aria-label={`Search ${title}`}
          onClick={openPopover}
          sx={{
            p: 0.25,
            color: 'text.secondary',
            opacity: 0.55,
            '&:hover': { opacity: 1, bgcolor: 'action.hover' },
          }}
        >
          <SearchIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        // The popover portal still bubbles React events into the header cell,
        // which would sort the column (click / Enter) or move focus (arrows).
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        // Restoring focus to the magnifier lets the closing Enter keystroke
        // re-trigger its click and instantly reopen the popover.
        disableRestoreFocus
        slotProps={{ paper: { sx: { p: 1, width: 220 } } }}
      >
        <HFTextField
          autoFocus
          fullWidth
          placeholder={`Search ${title}…`}
          size="small"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') apply(draft);
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
          <HFButton size="small" variant="text" onClick={() => apply('')}>
            Clear
          </HFButton>
          <HFButton size="small" onClick={() => apply(draft)}>
            Apply
          </HFButton>
        </Box>
      </Popover>
    </Box>
  );
}

export type HFDataTableToolbarProps = {
  placeholder?: string;
  /** Forwarded to MUI QuickFilter. Default 150; pass 0 when server search owns debounce. */
  debounceMs?: number;
  onQueryChange?: (query: string) => void;
};

/** Minimal quick-filter toolbar for global table search. */
export function HFDataTableToolbar({ placeholder = 'Search…', debounceMs, onQueryChange }: HFDataTableToolbarProps) {
  return (
    <Toolbar style={{ padding: '4px 8px', gap: 8, minHeight: 44, width: '100%' }}>
      <QuickFilter defaultExpanded debounceMs={debounceMs}>
        <QuickFilterControl
          render={(props) => {
            const { ref, value, onChange, ...other } = props as {
              ref?: Ref<HTMLInputElement>;
              value?: string;
              onChange?: (event: { target: { value: string } }) => void;
            } & Record<string, unknown>;
            return (
              <TextField
                {...other}
                value={value ?? ''}
                inputRef={ref}
                size="small"
                placeholder={placeholder}
                aria-label={placeholder}
                sx={{ minWidth: 220 }}
                onChange={(event) => {
                  onChange?.(event);
                  onQueryChange?.(event.target.value);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: value ? (
                      <InputAdornment position="end">
                        <QuickFilterClear edge="end" size="small" aria-label="Clear search" />
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />
            );
          }}
        />
      </QuickFilter>
    </Toolbar>
  );
}
